/**
 * `OrderMoneyBreakdown` — the ONE panel that renders an order's money
 * breakdown (per-line price, shipping, tax, total) across Held orders
 * (projected) and Activity (settled). Keeps the two surfaces visually
 * identical so a merchant reads the same order the same way whether
 * it is pending human review or already charged.
 *
 * Sources folded into this component:
 *   - UCP checkout breakdown (held: `item.breakdown`, settled:
 *     `record.order_breakdown`) — the richest shape, with per-line
 *     titles, shipping method name, and tax place.
 *   - Shopify pre-flight quote (held, `EscalationQuote`) — a projected
 *     all-in from the rail's calculate.
 *   - Settled rail evidence (`record.settlement` = `MandateRecord[settlement]`)
 *     — the money the rail actually charged.
 *
 * Minor units are integer cents; the component formats with `formatMinor`
 * (tabular-nums). Mono for amounts, uppercase mono kickers for labels —
 * mirrors the existing `SettlementPanel` density.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';

import { Pill, Tooltip } from '../../components/atoms/index.js';
import { formatMinor } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import type { EscalationQuote } from '../../api/escalations.js';
import type { MandateRecord } from './types.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type ThemeC = ReturnType<typeof useC>;

/**
 * The on-wire UCP breakdown shape (held + settled). Mirrors
 * `OrderBreakdown` in `services/api/src/ucp/order-breakdown.ts`.
 * NEVER mutated — the component only reads it.
 */
export interface UcpOrderBreakdown {
  readonly source: 'ucp_checkout';
  readonly currency: string;
  readonly line_items: readonly {
    readonly sku: string;
    readonly title: string;
    readonly quantity: number;
    readonly unit_price_minor: number;
    readonly line_total_minor: number;
  }[];
  readonly subtotal_minor: number;
  readonly discount_minor: number;
  /** ADR-0254 — the discount code that applied (shown on the Discount row). Optional on the wire. */
  readonly discount_code?: string | null;
  /** `null` when nothing in the order ships — the Shipping row is HIDDEN. */
  readonly shipping_minor: number | null;
  readonly shipping_title: string | null;
  /** `null` when no tax rule applied — the Tax row is HIDDEN. */
  readonly tax_minor: number | null;
  readonly tax_label: string | null;
  readonly total_minor: number;
  /**
   * Coarse ship-to from the checkout session's destination: ISO-2 country
   * code + optional region label (e.g. `{ country: 'US', region: 'TX' }`).
   * Street-level address stays behind the operator-click buyer reveal; this
   * is the non-PII slice the Overview + Activity surfaces render in a Ship
   * to row. `null` when the order carried no destination. Optional on the
   * wire — older rows that pre-date the field drop it rather than invent one.
   */
  readonly ship_to?: { readonly country: string; readonly region: string | null } | null;
  /**
   * The agent platform that placed the order — its UCP profile host (e.g.
   * `ops.sill.so`). Every UCP request is verified before Sill accepts it
   * (signature or merchant-issued access key — see `platform_auth`), so the
   * header "Placed by" and trust rows read it directly without an extra
   * fetch. Optional on the wire — legacy rows pre-date the field.
   */
  readonly platform_host?: string | null;
  /** ADR-0215 §11 — how the platform proved itself; optional on the wire. */
  readonly platform_auth?: 'signature' | 'api_key' | null;
}

/**
 * One parsed line of the shared breakdown. `unit_minor` + `line_total_minor`
 * are optional so held summaries without pricing can still render ("—" for
 * the amount).
 */
export interface OrderMoneyLine {
  readonly sku: string;
  readonly title?: string;
  readonly quantity: number;
  readonly unit_minor?: number;
  readonly line_total_minor?: number;
}

/**
 * Shared presentation shape. Any adapter (UCP / quote / settlement /
 * fallback) returns one of these; the component renders it the same way.
 *
 * `null` on `shipping_minor` or `tax_minor` HIDES the row (the data model
 * says "nothing ships" / "no tax rule matched"). An absent field (field
 * missing entirely) is also hidden. `undefined` on `total_minor` renders
 * "—" — we NEVER invent a total from the lines.
 */
export interface OrderMoney {
  readonly currency: string;
  readonly lines: readonly OrderMoneyLine[];
  readonly subtotal_minor?: number;
  readonly discount_minor?: number;
  readonly discount_code?: string | null;
  readonly shipping_minor?: number | null;
  readonly shipping_title?: string | null;
  readonly tax_minor?: number | null;
  readonly tax_label?: string | null;
  readonly total_minor?: number;
  readonly ceiling_minor?: number;
  readonly refunded_minor?: number;
}

export type OrderMoneyMode = 'projected' | 'settled';

export interface OrderMoneyBreakdownProps {
  readonly C: ThemeC;
  readonly mode: OrderMoneyMode;
  readonly money: OrderMoney;
}

const COLLAPSE_AFTER = 5;

/**
 * Adapter: UCP checkout breakdown → shared `OrderMoney`. The UCP shape is
 * already the richest form, so this is almost a pass-through with
 * `line_total_minor` + `unit_price_minor` surfaced per line.
 */
export function fromUcpBreakdown(b: UcpOrderBreakdown): OrderMoney {
  return {
    currency: b.currency,
    lines: b.line_items.map((li) => ({
      sku: li.sku,
      title: li.title,
      quantity: li.quantity,
      unit_minor: li.unit_price_minor,
      line_total_minor: li.line_total_minor,
    })),
    subtotal_minor: b.subtotal_minor,
    discount_minor: b.discount_minor,
    ...(b.discount_code !== undefined && b.discount_code !== null ? { discount_code: b.discount_code } : {}),
    shipping_minor: b.shipping_minor,
    shipping_title: b.shipping_title,
    tax_minor: b.tax_minor,
    tax_label: b.tax_label,
    total_minor: b.total_minor,
  };
}

/**
 * Adapter: the Shopify pre-flight quote → shared `OrderMoney`. The quote
 * exposes `shipping_minor: 0` to mean "no method" today; we preserve the
 * current UX ("hide when zero") rather than render a Free row. Tax is
 * always present on the quote shape (0 is a valid "no rule matched" on
 * Shopify dev stores) so it is kept.
 */
export function fromQuote(q: EscalationQuote): OrderMoney {
  return {
    currency: q.currency,
    lines: q.line_items.map((li) => ({
      sku: li.sku,
      title: li.product_title,
      quantity: li.quantity,
      line_total_minor: li.line_total_minor,
    })),
    subtotal_minor: q.subtotal_minor,
    discount_minor: q.discount_minor,
    shipping_minor: q.shipping_minor > 0 ? q.shipping_minor : null,
    shipping_title: q.chosen_rate_title,
    tax_minor: q.tax_minor,
    tax_label: null,
    total_minor: q.total_minor,
    ceiling_minor: q.ceiling_minor,
  };
}

/**
 * Adapter: settled rail evidence → shared `OrderMoney`. Mirrors the fields
 * `SettlementPanel` already reads from `MandateRecord['settlement']` — same
 * omit-when-zero discipline for shipping, same discount handling. Line
 * items carry the per-variant price (ADR-0078).
 */
export function fromSettlement(
  s: NonNullable<MandateRecord['settlement']>,
): OrderMoney {
  const currency = s.currency ?? 'USD';
  const lines: OrderMoneyLine[] =
    s.line_items !== undefined && s.line_items.length > 0
      ? s.line_items.map((li) => ({
          sku: li.sku,
          title: li.product_title,
          quantity: li.quantity,
          unit_minor: li.variant_price_minor,
          line_total_minor: li.line_total_minor,
        }))
      : [];
  // Settlement wire uses undefined when a field was not written (no
  // shipping row, no discount). We pass undefined through so the row
  // hides. Zero-value shipping stays hidden to match the current
  // SettlementPanel ("$0 shipping was NOT charged").
  const shipping = s.shipping_minor;
  const shipping_minor =
    typeof shipping === 'number' && Number.isFinite(shipping) && shipping > 0
      ? shipping
      : null;
  return {
    currency,
    lines,
    ...(typeof s.subtotal_minor === 'number' ? { subtotal_minor: s.subtotal_minor } : {}),
    ...(typeof s.discount_minor === 'number' && s.discount_minor > 0
      ? { discount_minor: s.discount_minor }
      : {}),
    shipping_minor,
    shipping_title: null,
    ...(typeof s.tax_minor === 'number' ? { tax_minor: s.tax_minor } : { tax_minor: null }),
    tax_label: null,
    ...(typeof s.total_minor === 'number' ? { total_minor: s.total_minor } : {}),
    ...(typeof s.ceiling_minor === 'number' ? { ceiling_minor: s.ceiling_minor } : {}),
    ...(typeof s.refunded_minor === 'number' && s.refunded_minor > 0
      ? { refunded_minor: s.refunded_minor }
      : {}),
  };
}

/**
 * One-line summary a list row renders when it has the breakdown or
 * settlement data: `"Felt Hat · $47.06"` on a single-line order,
 * `"Felt Hat + 2 more · $182.40"` on multi-line (the first product name a
 * merchant actually recognises, plus the count of the rest — more useful
 * than "3 items" when triaging a queue). `—` for the amount when the total
 * is unknown. Returns `null` when there is nothing meaningful to say.
 */
export function orderSummaryLine(money: OrderMoney): string | null {
  const label = productSummaryLabel(money.lines);
  const total =
    typeof money.total_minor === 'number'
      ? formatMinor(money.total_minor, money.currency)
      : '—';
  if (label === null) return total === '—' ? null : total;
  return `${label} · ${total}`;
}

/**
 * Short product label for a list row: the first product's title (optionally
 * `qty × Title` on a single-line order) + `+ N more` when the cart carries
 * additional lines. Returns `null` when the breakdown has no usable lines
 * at all. Used by both `orderSummaryLine` and the held-orders list row.
 */
export function productSummaryLabel(lines: readonly OrderMoneyLine[]): string | null {
  if (lines.length === 0) return null;
  const first = lines[0]!;
  const firstLabel = first.title ?? first.sku;
  if (lines.length === 1) {
    return first.quantity > 1 ? `${first.quantity} × ${firstLabel}` : firstLabel;
  }
  return `${firstLabel} + ${lines.length - 1} more`;
}

/**
 * The breakdown panel. Rows in order:
 *   per line: `qty × Name` · line total · muted SKU + unit price under
 *   Subtotal
 *   Discount (negative, only when > 0)
 *   Shipping · <title>  — "Free" when 0; row HIDDEN when `shipping_minor === null`
 *   Tax · <label>       — row HIDDEN when `tax_minor === null`
 *   ──────── 1px rule
 *   Total               — emphasised
 *   Authorized max      — muted; projected mode only
 *   Over by $X          — coral; only when total > ceiling
 *   Refunded −$X        — coral; only on refunded orders
 *   Net                 — muted; derived (total − refunded)
 */
export function OrderMoneyBreakdown({
  C,
  mode,
  money,
}: OrderMoneyBreakdownProps): JSX.Element {
  const { currency } = money;
  const [expanded, setExpanded] = useState(false);

  const visibleLines =
    expanded || money.lines.length <= COLLAPSE_AFTER
      ? money.lines
      : money.lines.slice(0, COLLAPSE_AFTER);
  const hiddenCount = money.lines.length - visibleLines.length;

  const fmt = (minor: number | undefined | null): string =>
    typeof minor === 'number' ? formatMinor(minor, currency) : '—';

  // Over-ceiling: a projected total that would exceed the agent-signed cap.
  const over_by =
    mode === 'projected'
    && typeof money.total_minor === 'number'
    && typeof money.ceiling_minor === 'number'
    && money.total_minor > money.ceiling_minor
      ? money.total_minor - money.ceiling_minor
      : null;

  // Settled net after a partial / full refund (floored at 0).
  const net_minor =
    typeof money.refunded_minor === 'number'
    && money.refunded_minor > 0
    && typeof money.total_minor === 'number'
      ? Math.max(0, money.total_minor - money.refunded_minor)
      : null;

  return (
    <div
      data-testid="order-money-breakdown"
      data-mode={mode}
      className="p-3 space-y-2"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          ORDER
        </span>
        {mode === 'projected' ? (
          over_by === null ? (
            // Founder 2026-10-21: "PROJECTED" read as a chart term. The
            // merchant question is simpler — "has money moved?". The pill
            // now answers that, with a tooltip spelling out what runs on
            // approval. "Authorized max" semantics live on their own row
            // below (unchanged).
            <Tooltip content="These are the order's prices, shipping and tax as quoted. Nothing is charged until the order is approved and the platform completes payment.">
              <Pill tone="sky">NOT CHARGED YET</Pill>
            </Tooltip>
          ) : (
            <Pill tone="coral">{`OVER BY ${fmt(over_by)}`}</Pill>
          )
        ) : null}
      </div>

      {money.lines.length > 0 ? (
        <div
          className="space-y-2"
          style={{
            paddingTop: 2,
            paddingBottom: 2,
          }}
        >
          {visibleLines.map((li, i) => (
            <OrderLineRow key={`${li.sku}-${i}`} C={C} line={li} currency={currency} />
          ))}
          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-left"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1.5,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              + {hiddenCount} more
            </button>
          ) : null}
        </div>
      ) : null}

      {money.lines.length > 0
      && (money.subtotal_minor !== undefined
        || money.discount_minor !== undefined
        || money.shipping_minor !== undefined
        || money.tax_minor !== undefined
        || money.total_minor !== undefined) ? (
        <div style={{ borderTop: `1px solid ${C.borderSoft}` }} />
      ) : null}

      <dl className="space-y-1" style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
        {money.subtotal_minor !== undefined ? (
          <MoneyRow C={C} label="SUBTOTAL" amount={fmt(money.subtotal_minor)} />
        ) : null}

        {typeof money.discount_minor === 'number' && money.discount_minor > 0 ? (
          <MoneyRow C={C} label={typeof money.discount_code === 'string' && money.discount_code.length > 0 ? `DISCOUNT · ${money.discount_code}` : 'DISCOUNT'} amount={`−${fmt(money.discount_minor)}`} />
        ) : null}

        {money.shipping_minor !== null && money.shipping_minor !== undefined ? (
          <MoneyRow
            C={C}
            label={shippingLabel(money.shipping_title)}
            amount={money.shipping_minor === 0 ? 'Free' : fmt(money.shipping_minor)}
          />
        ) : null}

        {money.tax_minor !== null && money.tax_minor !== undefined ? (
          <MoneyRow C={C} label={taxLabel(money.tax_label)} amount={fmt(money.tax_minor)} />
        ) : null}

        {money.total_minor !== undefined ? (
          <TotalRow C={C} amount={fmt(money.total_minor)} coral={over_by !== null} />
        ) : null}

        {mode === 'projected' && money.ceiling_minor !== undefined ? (
          <MoneyRow C={C} label="AUTHORIZED MAX" amount={fmt(money.ceiling_minor)} muted />
        ) : null}

        {typeof money.refunded_minor === 'number' && money.refunded_minor > 0 ? (
          <MoneyRow
            C={C}
            label="REFUNDED"
            amount={`−${fmt(money.refunded_minor)}`}
            tone="coral"
          />
        ) : null}

        {net_minor !== null ? <MoneyRow C={C} label="NET" amount={fmt(net_minor)} muted /> : null}
      </dl>

      {over_by !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          Approving will not charge the buyer — settlement fails closed when
          the total exceeds the authorized maximum. The order would be lost;
          the agent must re-sign with a higher max amount.
        </div>
      ) : null}
    </div>
  );
}

function shippingLabel(title: string | null | undefined): string {
  if (typeof title === 'string' && title.trim().length > 0) {
    return `SHIPPING · ${title.toUpperCase()}`;
  }
  return 'SHIPPING';
}

function taxLabel(label: string | null | undefined): string {
  if (typeof label === 'string' && label.trim().length > 0) {
    return `TAX · ${label.toUpperCase()}`;
  }
  return 'TAX';
}

function OrderLineRow({
  C,
  line,
  currency,
}: {
  C: ThemeC;
  line: OrderMoneyLine;
  currency: string;
}): JSX.Element {
  const label = line.title ?? line.sku;
  const line_total =
    typeof line.line_total_minor === 'number' ? formatMinor(line.line_total_minor, currency) : '—';
  const unit_price =
    typeof line.unit_minor === 'number' ? formatMinor(line.unit_minor, currency) : null;
  return (
    <div className="flex items-start justify-between gap-3">
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, wordBreak: 'break-word' }}>
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
            {line.quantity} ×
          </span>{' '}
          {label}
          {unit_price !== null && line.quantity > 1 ? (
            <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginLeft: 6 }}>
              @ {unit_price}
            </span>
          ) : null}
        </div>
        {line.title !== undefined && line.title !== line.sku ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              wordBreak: 'break-all',
              marginTop: 1,
            }}
          >
            {line.sku}
          </div>
        ) : null}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
          textAlign: 'right',
          paddingTop: 1,
        }}
      >
        {line_total}
      </div>
    </div>
  );
}

interface MoneyRowProps {
  readonly C: ThemeC;
  readonly label: string;
  readonly amount: ReactNode;
  readonly muted?: boolean;
  readonly tone?: 'coral';
}

function MoneyRow({ C, label, amount, muted, tone }: MoneyRowProps): JSX.Element {
  const color = tone === 'coral' ? C.coralFg : muted ? C.muted : C.text;
  return (
    <div className="flex items-baseline justify-between">
      <dt
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color,
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
      >
        {amount}
      </dd>
    </div>
  );
}

function TotalRow({ C, amount, coral }: { C: ThemeC; amount: ReactNode; coral: boolean }): JSX.Element {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{
        paddingTop: 6,
        marginTop: 4,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <dt
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.text,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        TOTAL
      </dt>
      <dd
        style={{
          fontFamily: monoFont,
          fontSize: 14,
          color: coral ? C.coralFg : C.text,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
      >
        {amount}
      </dd>
    </div>
  );
}
